<script setup>
import { formatTelLink } from '@/composables/useFormatTelLink';
import { useBlockSafe } from '@/composables/useBlockSafe';
import Buttons from '@/components/ui/Buttons.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  description,
  image,
  buttons,
  contacts,
  hasTitle,
  hasDescription,
  hasImage,
  hasButtons,
  hasContacts,
  hasAnyContent,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
      backgroundColor === 'black' ? 'white' : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="image-text-block max-w-784">
        <NuxtImg
          v-if="hasImage"
          :src="image.src"
          :alt="image.alt"
          :width="image.width"
          :height="image.height"
          class="image-text-block__image"
          data-aos="zoom-in"
          data-aos-easing="ease-out-quad"
          loading="lazy"
        />
        <div
          v-if="hasTitle || hasDescription || hasButtons"
          class="image-text-block__bottom"
        >
          <h2
            v-if="hasTitle"
            class="mb-0 image-text-block__title h2"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="title"
          ></h2>
          <div
            v-if="hasDescription"
            class="image-text-block__text h2 body-1-regular"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="description"
          ></div>
          <div
            v-if="hasContacts"
            class="image-text-block__contacts"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            <template
              v-for="(contactItem, contactItemIndex) in contacts"
              :key="contactItemIndex"
            >
              <template v-if="contactItem.value">
                <template v-if="contactItem.type === 'phone'">
                  <a
                    :href="`tel:${formatTelLink(contactItem.value)}`"
                    class="image-text-block__contact"
                  >
                    <CIcon
                      class="image-text-block__contact-icon"
                      name="phone-red"
                      width="24"
                      height="24"
                    />{{ contactItem.value }}</a
                  >
                </template>
                <template v-else>
                  <a
                    :href="`mailto:${contactItem.value}`"
                    class="image-text-block__contact"
                  >
                    <CIcon
                      class="image-text-block__contact-icon"
                      name="email-red"
                      width="24"
                      height="24"
                    />{{ contactItem.value }}</a
                  >
                </template>
              </template>
            </template>
          </div>
          <Buttons
            v-if="hasButtons"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            class="image-text-block__buttons"
            :background-color="backgroundColor"
            :buttons="buttons"
            :buttons-prefix-class="'js-button-image-text-block'"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.image-text-block {
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin: 0 auto;
  width: 100%;
  @include media-breakpoint-up(sm) {
    gap: 64px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 64);
  }
  &__title {
    margin: 0;
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 32px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__image {
    border-radius: 4px;
    margin: 0 auto;
    height: auto;
    width: 45%;
    @include media-breakpoint-up(sm) {
      width: 23%;
    }
    @include media-breakpoint-up(md) {
      width: 21.5%;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 4);
    }
  }
  &__text {
    @include media-breakpoint-up(sm) {
      margin: 0 auto;
      max-width: 68%;
    }
  }
  &__contacts {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      flex-direction: row;
      gap: 30px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 30);
    }
  }
  &__contact {
    @include link-large;
    align-items: center;
    align-self: center;
    display: grid;
    gap: 8px;
    grid-template-columns: 24px auto;
    max-width: fit-content;
    @include media-breakpoint-up(md) {
      align-self: self-start;
      gap: 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
      grid-template-columns: px-to-vw(24) auto;
    }
    &-icon {
      height: 100%;
      width: 100%;
    }
  }
  &__buttons {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    @include media-breakpoint-up(sm) {
      flex-direction: row;
      gap: 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
}
</style>
